import * as React from "react";
import { Link } from "@tanstack/react-router";
import * as m from "~/paraglide/messages";
import { icons } from "./SidebarIcons";

/* ── Phase-based navigation config ── */

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
}

interface NavPhase {
  id: string;
  label: string;
  items: NavItem[];
}

function getStandaloneTop(): NavItem[] {
  return [
    { to: "/dashboard", label: m.nav_dashboard(), icon: icons.dashboard },
  ];
}

function getPhases(): NavPhase[] {
  return [
    {
      id: "prepare",
      label: m.nav_phase_prepare(),
      items: [
        { to: "/resumes", label: m.nav_resumes(), icon: icons.resumes },
        { to: "/keyword-optimizer", label: m.nav_keyword_optimizer(), icon: icons.keywordOptimizer },
        { to: "/story-bank", label: m.nav_story_bank(), icon: icons.storyBank },
      ],
    },
    {
      id: "discover",
      label: m.nav_phase_discover(),
      items: [
        { to: "/jobs", label: m.nav_jobs(), icon: icons.jobs },
        { to: "/portal-scanner", label: m.nav_portal_scanner(), icon: icons.portalScanner },
        { to: "/deep-match", label: m.nav_deep_match(), icon: icons.deepMatch },
        { to: "/salary", label: m.nav_salary(), icon: icons.salary },
      ],
    },
    {
      id: "apply",
      label: m.nav_phase_apply(),
      items: [
        { to: "/applications", label: m.nav_applications(), icon: icons.applications },
        { to: "/cover-letters", label: m.nav_cover_letters(), icon: icons.coverLetters },
      ],
    },
    {
      id: "interview",
      label: m.nav_phase_interview(),
      items: [
        { to: "/interview-prep", label: m.nav_interview(), icon: icons.interview },
        { to: "/negotiation", label: m.nav_negotiation(), icon: icons.negotiation },
      ],
    },
  ];
}

function getStandaloneMiddle(): NavItem[] {
  return [
    { to: "/analytics", label: m.nav_analytics(), icon: icons.analytics },
  ];
}

function getResourceItems(): NavItem[] {
  return [
    { to: "/blog", label: m.nav_blog(), icon: icons.blog },
  ];
}

function getSupportItems(): NavItem[] {
  return [
    { to: "/help", label: m.nav_help_center(), icon: icons.helpCenter },
    { to: "/feedback", label: m.nav_feedback(), icon: icons.feedback },
  ];
}

/* ── Sidebar nav with roving tabindex (A11Y-11) ── */

export function SidebarNav({ onLinkClick }: { onLinkClick: () => void }) {
  const navRef = React.useRef<HTMLElement>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const standaloneTop = getStandaloneTop();
  const phases = getPhases();
  const standaloneMiddle = getStandaloneMiddle();
  const resourceItems = getResourceItems();
  const supportItems = getSupportItems();

  let globalIndex = 0;

  function handleKeyDown(e: React.KeyboardEvent) {
    const nav = navRef.current;
    if (!nav) return;
    const links = nav.querySelectorAll<HTMLAnchorElement>("a.nav-item");
    const count = links.length;
    if (count === 0) return;

    let nextIndex = activeIndex;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        nextIndex = (activeIndex + 1) % count;
        break;
      case "ArrowUp":
        e.preventDefault();
        nextIndex = (activeIndex - 1 + count) % count;
        break;
      case "Home":
        e.preventDefault();
        nextIndex = 0;
        break;
      case "End":
        e.preventDefault();
        nextIndex = count - 1;
        break;
      default:
        return;
    }

    setActiveIndex(nextIndex);
    links[nextIndex]?.focus();
  }

  function renderLink(item: NavItem) {
    const idx = globalIndex++;
    return (
      <Link
        key={item.to}
        to={item.to}
        className="nav-item"
        activeProps={{ className: "nav-item active" }}
        tabIndex={activeIndex === idx ? 0 : -1}
        onFocus={() => setActiveIndex(idx)}
        onClick={onLinkClick}
      >
        {item.icon}
        <span>{item.label}</span>
      </Link>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- arrow-key navigation for links
    <nav
      ref={navRef}
      className="flex-1 overflow-y-auto px-2 py-3"
      aria-label="Sidebar"
      onKeyDown={handleKeyDown}
    >
      {/* Dashboard — top-level, no phase */}
      <div className="flex flex-col gap-0.5">
        {standaloneTop.map((item) => renderLink(item))}
      </div>

      {/* Lifecycle phases */}
      {phases.map((phase) => (
        <div key={phase.id} className="mt-4" role="group" aria-labelledby={`sidebar-phase-${phase.id}`}>
          <h3
            id={`sidebar-phase-${phase.id}`}
            className="section-label px-2 pb-1"
            style={{ margin: 0 }}
          >
            {phase.label}
          </h3>
          <div className="flex flex-col gap-0.5 pl-2">
            {phase.items.map((item) => renderLink(item))}
          </div>
        </div>
      ))}

      {/* Analytics — cross-cutting, standalone */}
      <div className="my-2 h-px" style={{ background: "var(--border-subtle)" }} />
      <div className="flex flex-col gap-0.5">
        {standaloneMiddle.map((item) => renderLink(item))}
      </div>

      {/* Resources (Blog, guides) */}
      <div className="my-2 h-px" style={{ background: "var(--border-subtle)" }} />
      <div className="flex flex-col gap-0.5" role="group" aria-labelledby="sidebar-resources-heading">
        <h3 id="sidebar-resources-heading" className="section-label px-2 pb-1" style={{ margin: 0 }}>
          {m.nav_resources()}
        </h3>
        <div className="flex flex-col gap-0.5 pl-2">
          {resourceItems.map((item) => renderLink(item))}
        </div>
      </div>

      {/* Support */}
      <div className="my-2 h-px" style={{ background: "var(--border-subtle)" }} />
      <div className="flex flex-col gap-0.5" role="group" aria-labelledby="sidebar-support-heading">
        <h3 id="sidebar-support-heading" className="section-label px-2 pb-1" style={{ margin: 0 }}>
          {m.nav_support()}
        </h3>
        <div className="flex flex-col gap-0.5 pl-2">
          {supportItems.map((item) => renderLink(item))}
        </div>
      </div>
    </nav>
  );
}
